// Pure helpers для BiomarkerChart (C-S5-3 recharts).
// Source of truth: docs/specs/sprint-5/stage-4-frontend.md §4.3 + T-S5-13 vitest scope.
//
// Обязательства:
//   - shapeData() оставляет ASC порядок backend'а (никаких локальных сортировок).
//   - fmtDate() — RU локализация месяцев через Intl.DateTimeFormat('ru-RU').
//   - computeDomain() добавляет ±10% padding с учётом normal range.

import type { BiomarkerDetailPoint, TimelinePeriod } from '@/api/types';
import { displayTier, type DisplayTier } from '@/lib/biomarkerStatus';

export type ChartDatum = {
  x: string; // ISO date 'YYYY-MM-DD'
  y: number; // value_normalized
  // HEAL-35: тир, а не clinical_status. У графика был свой словарь из трёх цветов,
  // в котором оранжевого не существовало — значение «в норме, но не оптимум»
  // рисовалось зелёной точкой под оранжевой шапкой карточки.
  tier: DisplayTier;
  // HEAL-33: у качественного показателя по оси Y стоит ранг статуса (0/1/2), а не
  // измеренная величина — у «Тёмно-жёлтого» нет числового расстояния до «Красного».
  // Само слово подписываем у точки; число пользователю не показываем никогда.
  word: string | null;
  uploadId: number;
  labSource: string | null;
};

/** Подписи оси Y для качественного графика — вместо 0/1/2. */
export const RANK_LABEL: Record<number, string> = {
  0: 'Норма',
  1: 'Внимание',
  2: 'Отклонение',
};

/** График качественный, если ответ словом есть хотя бы у одной точки. */
export function isQualitativeSeries(data: ChartDatum[]): boolean {
  return data.some((d) => d.word !== null);
}

export function shapeData(
  points: BiomarkerDetailPoint[],
  _period: TimelinePeriod,
): ChartDatum[] {
  return points.map((p) => ({
    x: p.measured_at,
    y: p.value_normalized,
    tier: displayTier(p.clinical_status, p.optimum_status),
    word: p.value_qualitative,
    uploadId: p.upload_id,
    labSource: p.lab_source,
  }));
}

const DM = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const MY = new Intl.DateTimeFormat('ru-RU', { month: 'short', year: 'numeric' });
const FULL = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export function fmtDate(period: TimelinePeriod): (iso: string) => string {
  const formatter = period === '3m' || period === '6m' ? DM : MY;
  return (iso) => formatter.format(new Date(iso));
}

export function formatRuDate(iso: string): string {
  return FULL.format(new Date(iso));
}

export function computeDomain(
  points: BiomarkerDetailPoint[],
  normalMin: number | null,
  normalMax: number | null,
): [number, number] {
  if (points.length === 0) {
    if (normalMin !== null && normalMax !== null) {
      return [normalMin, normalMax];
    }
    return [0, 1];
  }
  const values = points.map((p) => p.value_normalized);
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (normalMin !== null) min = Math.min(min, normalMin);
  if (normalMax !== null) max = Math.max(max, normalMax);
  const padding = (max - min) * 0.1 || max * 0.1 || 1;
  // Округляем границы, иначе float-арифметика даёт хвосты вроде
  // 1.6219999999999999, которые recharts печатает на оси Y целиком.
  return [round2(min - padding), round2(max + padding)];
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
